import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';

interface EditableFieldProps {
  initialValue: string | number | null | undefined;
  onSave: (value: string) => Promise<void>;
  type?: 'text' | 'number';
  placeholder?: string;
}

const toText = (v: string | number | null | undefined) =>
  v === null || v === undefined ? '' : String(v);

/** Inline text/number input with a Save button that lights up only when the value changed. */
const EditableField: React.FC<EditableFieldProps> = ({
  initialValue,
  onSave,
  type = 'text',
  placeholder,
}) => {
  const [value, setValue] = useState(toText(initialValue));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setValue(toText(initialValue));
  }, [initialValue]);

  const hasChanged = value !== toText(initialValue);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(value);
    } catch {
      // parent shows the alert
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          let val = e.target.value;
          if (type === 'number') val = val.replace(/[^0-9.]/g, '');
          setValue(val);
        }}
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'var(--text-primary)',
          padding: '4px 8px',
          borderRadius: '4px',
          width: '100%',
          minWidth: 80,
          outline: 'none',
        }}
      />
      <Button
        size="small"
        variant="contained"
        disabled={!hasChanged || isSaving}
        onClick={handleSave}
        sx={{
          backgroundColor: '#4caf50',
          color: '#fff',
          textTransform: 'none',
          minWidth: 'auto',
          padding: '2px 10px',
          '&:hover': { backgroundColor: '#43a047' },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.3)',
          },
          flexShrink: 0,
        }}
      >
        Save
      </Button>
    </Box>
  );
};

export default EditableField;
